import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CaretRight, CreditCard, DeviceMobile, Tag } from "@phosphor-icons/react";
import MapView from "../components/MapView";
import { Button, BrandMark } from "../components/ui-kit";

const BUNIA_CENTER = { lat: 1.5667, lng: 30.25 };
const PICKUP = { lat: 1.5667, lng: 30.25 };
const DESTINATION = { lat: 1.5803, lng: 30.2605 };
const DRIVER = { lat: 1.5718, lng: 30.2548 };
const CAR_MARKERS = [
  { top: "16%", right: "14%", rotate: "20deg" },
  { top: "24%", right: "8%", rotate: "-12deg" },
  { top: "34%", right: "18%", rotate: "7deg" },
  { top: "43%", right: "10%", rotate: "-18deg" },
];

const DEVICE_PINS = [
  { name: "Pixel", top: "26%", left: "18%", tone: "bg-primary text-primary-foreground" },
  { name: "A14", top: "38%", left: "58%", tone: "bg-secondary text-secondary-foreground" },
  { name: "S24", top: "50%", left: "30%", tone: "bg-black text-white" },
  { name: "Fold", top: "18%", left: "62%", tone: "bg-white text-black" },
];

const DEVICES = [
  { name: "Pixel 8", size: "Compact", width: 360, height: 780, preview: 430 },
  { name: "Galaxy A14", size: "Standard", width: 390, height: 844, preview: 455 },
  { name: "Galaxy S24 Ultra", size: "Large", width: 430, height: 932, preview: 485 },
  { name: "Galaxy Z Fold", size: "Wide", width: 520, height: 720, preview: 410 },
];

function AndroidFrame({ device }) {
  const isFold = device.name.includes("Fold");

  return (
    <section className="min-w-0">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-heading text-lg font-bold">{device.name}</div>
          <div className="font-mono text-[11px] font-bold text-muted-foreground">
            {device.width} x {device.height} - {device.size}
          </div>
        </div>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
          <DeviceMobile size={20} weight="duotone" />
        </div>
      </div>

      <div
        className="relative mx-auto overflow-hidden rounded-[2rem] border border-white/15 bg-black p-2 shadow-2xl"
        style={{ width: "min(100%, 100%)", maxWidth: Math.min(device.width, 430) }}
      >
        <div className="absolute left-1/2 top-2 z-[900] h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-black" />
        <div
          className="relative overflow-hidden rounded-[1.5rem] bg-white"
          data-testid="android-frame-inner"
          style={{ height: device.preview, maxHeight: "72vh" }}
        >
          <div className="absolute inset-x-0 top-0 z-[850] flex h-7 items-center justify-between px-4 text-[10px] font-bold text-neutral-800">
            <div className="flex items-center gap-1">
              <span className="h-2 w-3 rounded-sm border border-neutral-700" />
              <span className="h-2 w-2 rounded-full border border-neutral-700" />
            </div>
            <span>16:53</span>
          </div>

          <div className={`absolute inset-x-0 top-0 ${isFold ? "h-[67%]" : "h-[65%]"}`}>
            <MapView
              center={BUNIA_CENTER}
              zoom={13}
              pickup={PICKUP}
              destination={DESTINATION}
              driver={DRIVER}
              height="100%"
              useRouting={false}
              tileTheme="auto"
              routeColor="#0A0A0A"
              routeDashArray=""
              showAttribution={false}
              minHeight={0}
              className="absolute inset-0"
            />
            <div className="pointer-events-none absolute inset-0 z-[855]">
              {DEVICE_PINS.map((pin) => (
                <div
                  key={pin.name}
                  className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-full bg-white/90 p-1 pr-2 text-[9px] font-black uppercase tracking-wide text-black shadow-lg"
                  style={{ top: pin.top, left: pin.left }}
                >
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full ${pin.tone}`}>
                    <DeviceMobile size={13} weight="fill" />
                  </span>
                  {pin.name}
                </div>
              ))}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 top-8 z-[860] flex items-start justify-between px-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-xl font-black text-black shadow">
              x
            </div>
            <div className="flex overflow-hidden rounded-lg bg-white text-neutral-950 shadow-lg">
              <div className="bg-black px-3 py-2 text-center font-mono text-xs font-black text-white">
                <div className="text-lg leading-none">6</div>
                <div className="text-[8px] uppercase">min</div>
              </div>
              <div className="flex items-center gap-1 px-3 py-2 text-sm font-bold">
                Mudzipela <CaretRight size={14} weight="bold" />
              </div>
            </div>
          </div>

          {CAR_MARKERS.map((car, index) => (
            <div
              key={index}
              className="pointer-events-none absolute z-[850] h-8 w-5 rounded-md border border-neutral-400 bg-white shadow-md"
              style={{ top: car.top, right: car.right, transform: `rotate(${car.rotate})` }}
            >
              <div className="mx-auto mt-1 h-3 w-3 rounded-sm bg-neutral-900/80" />
            </div>
          ))}

          <div className="pointer-events-none absolute bottom-[34%] left-5 z-[860] flex overflow-hidden rounded-lg bg-white text-neutral-950 shadow-lg">
            <div className="flex h-7 w-7 items-center justify-center bg-black text-xs font-black text-white">A</div>
            <div className="flex items-center gap-1 px-3 text-xs font-bold">
              Centre-Ville <CaretRight size={12} weight="bold" />
            </div>
          </div>

          <div className="pointer-events-none absolute bottom-[calc(33%+12px)] right-4 z-[860] flex h-11 w-11 items-center justify-center rounded-full bg-white text-black shadow-lg">
            <div className="h-5 w-5 rounded-full border-4 border-black">
              <div className="mx-auto mt-1 h-1.5 w-1.5 rounded-full bg-black" />
            </div>
          </div>

          <div className={`absolute inset-x-0 bottom-0 z-[870] bg-white text-neutral-950 ${isFold ? "h-[33%]" : "h-[35%]"}`}>
            <div className="flex items-center justify-center gap-2 border-b border-neutral-200 py-1.5 text-[11px] text-neutral-700">
              <Tag size={15} weight="fill" className="text-emerald-600" />
              Promotion de 25% appliquee
            </div>

            <div className="px-4 py-2.5">
              <div className="mx-auto h-10 w-20 rounded-[40%] bg-neutral-100 shadow-inner">
                <div className="mx-auto translate-y-3 h-6 w-14 rounded-t-2xl rounded-b-md bg-white shadow-lg">
                  <div className="mx-auto h-2 w-7 rounded-b-xl bg-neutral-900" />
                </div>
              </div>

              <div className="mt-2 flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-2xl font-black leading-none">Medics</div>
                  <div className="mt-1 text-xs font-bold text-neutral-600">4 places</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1 text-2xl font-black">
                    <Tag size={18} weight="fill" className="text-emerald-600" />
                    9,20
                  </div>
                  <div className="text-xs font-bold text-neutral-400 line-through">12,27</div>
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between border-t border-neutral-100 pt-2">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-10 items-center justify-center rounded-md bg-neutral-900 text-white">
                    <CreditCard size={20} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-xs font-bold">MasterCard 3859</div>
                    <div className="text-[11px] font-bold text-emerald-700">Vous economisez 3,07</div>
                  </div>
                </div>
                <CaretRight size={18} weight="bold" />
              </div>

              <button className="mt-2 h-10 w-full bg-black text-xs font-black uppercase tracking-wide text-white">
                Payer Medics
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AndroidMapPreview() {
  return (
    <main className="app-screen bg-premium-grad px-4 pb-4 text-foreground sm:px-6">
      <header className="public-header-safe mx-auto flex max-w-7xl items-center justify-between gap-3">
        <BrandMark className="public-header-brand" />
        <Link to="/">
          <Button variant="outline" className="text-xs">
            <ArrowLeft size={16} weight="bold" />
            Retour
          </Button>
        </Link>
      </header>

      <section className="mx-auto mt-5 max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-primary">
              Carte Android
            </div>
            <h1 className="mt-1 font-heading text-3xl font-bold leading-tight sm:text-4xl">
              Apercu multi-ecrans
            </h1>
          </div>
          <div className="rounded-full glass-strong px-4 py-2 font-mono text-xs font-bold text-muted-foreground">
            4 formats - Android
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {DEVICES.map((device) => (
            <AndroidFrame key={device.name} device={device} />
          ))}
        </div>
      </section>
    </main>
  );
}
